/**
 * Global registry of open bottom sheets for back-gesture handling.
 *
 * On Capacitor (iOS/Android), the native back button / swipe-back gesture fires
 * a CapacitorApp `backButton` event, which is handled by useSwipeBackGesture.
 * That hook checks this registry first — if any sheet is open it closes the
 * topmost one instead of navigating away.
 *
 * On web browsers, BottomSheet pushes a history entry when opening and listens
 * for `popstate` directly (no registry needed).
 */

const stack: Array<() => void> = [];

/**
 * Register an open sheet's close callback. Returns an unregister function.
 * Call the returned function when the sheet closes (or use it as useEffect cleanup).
 */
export const registerSheet = (onClose: () => void): (() => void) => {
  stack.push(onClose);
  return () => {
    const idx = stack.lastIndexOf(onClose);
    if (idx !== -1) stack.splice(idx, 1);
  };
};

/**
 * If any sheet is open, close the topmost one and return true.
 * Returns false if no sheets are registered (caller should handle normally).
 */
export const handleSheetBack = (): boolean => {
  if (stack.length === 0) return false;
  stack[stack.length - 1]();
  return true;
};
